const CHAT_CONVERSATION_TOKEN_PREFIX = "kig-chat-conversation-token";
const CHAT_CONVERSATION_FEEDBACK_PREFIX = "kig-chat-conversation-feedback";

export const CHAT_CONVERSATION_SESSION_TTL_MS = 30 * 60 * 1000;

const memoryConversationSessions = new Map();
const getConversationOwner = (userId) => userId || "current-user";
const getConversationScope = (scopeKey) => scopeKey || "general";

export const getChatConversationTokenStorageKey = (userId, scopeKey) =>
  `${CHAT_CONVERSATION_TOKEN_PREFIX}:${getConversationOwner(userId)}:${getConversationScope(scopeKey)}`;

export const createChatConversationToken = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const isActiveConversationSession = (session, now) =>
  session &&
  typeof session.token === "string" &&
  session.token.trim() &&
  Number.isFinite(session.expiresAt) &&
  session.expiresAt > now;

const readConversationSession = (userId, scopeKey, now = Date.now()) => {
  const storageKey = getChatConversationTokenStorageKey(userId, scopeKey);

  try {
    const stored = sessionStorage.getItem(storageKey);
    const session = stored ? JSON.parse(stored) : null;

    if (isActiveConversationSession(session, now)) {
      memoryConversationSessions.set(storageKey, session);
      return session;
    }

    sessionStorage.removeItem(storageKey);
  } catch {
    // Pamięciowa kopia sesji pozwala kontynuować rozmowę bez sessionStorage.
  }

  const memorySession = memoryConversationSessions.get(storageKey);
  if (isActiveConversationSession(memorySession, now)) {
    return memorySession;
  }

  memoryConversationSessions.delete(storageKey);
  return null;
};

const saveConversationSession = (userId, scopeKey, session) => {
  const storageKey = getChatConversationTokenStorageKey(userId, scopeKey);
  memoryConversationSessions.set(storageKey, session);

  try {
    sessionStorage.setItem(storageKey, JSON.stringify(session));
  } catch {
    // Kopia w pamięci nadal grupuje wiadomości w ramach bieżącej karty.
  }
};

export const getChatConversationToken = (userId, scopeKey) =>
  readConversationSession(userId, scopeKey)?.token || null;

export const getOrCreateChatConversationToken = (userId, scopeKey) => {
  const now = Date.now();
  const activeSession = readConversationSession(userId, scopeKey, now);
  const token = activeSession?.token || createChatConversationToken();

  saveConversationSession(userId, scopeKey, {
    token,
    expiresAt: now + CHAT_CONVERSATION_SESSION_TTL_MS,
  });

  return token;
};

const createEmptyFeedback = (conversationToken) => ({
  conversationToken,
  rating: null,
  comment: "",
  updatedAt: null,
});

export const getChatConversationFeedback = (conversationToken) => {
  if (!conversationToken) {
    return createEmptyFeedback(conversationToken);
  }

  try {
    const stored = sessionStorage.getItem(
      `${CHAT_CONVERSATION_FEEDBACK_PREFIX}:${conversationToken}`,
    );
    const feedback = stored ? JSON.parse(stored) : null;

    if (
      feedback?.conversationToken === conversationToken &&
      ["positive", "negative"].includes(feedback.rating)
    ) {
      return {
        conversationToken,
        rating: feedback.rating,
        comment: typeof feedback.comment === "string" ? feedback.comment : "",
        updatedAt:
          typeof feedback.updatedAt === "string" ? feedback.updatedAt : null,
      };
    }
  } catch {
    // Brak dostępu do sessionStorage nie powinien blokować działania czatu.
  }

  return createEmptyFeedback(conversationToken);
};

export const saveChatConversationFeedback = (
  conversationToken,
  { rating, comment = "" },
) => {
  const feedback = {
    conversationToken,
    rating,
    comment: rating === "negative" ? comment.trim() : "",
    updatedAt: new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(
      `${CHAT_CONVERSATION_FEEDBACK_PREFIX}:${conversationToken}`,
      JSON.stringify(feedback),
    );
  } catch {
    // Stan React nadal przechowuje ocenę, gdy sessionStorage jest niedostępny.
  }

  return feedback;
};

export const clearChatConversationSession = (
  userId,
  conversationToken,
  scopeKey,
) => {
  const storageKey = getChatConversationTokenStorageKey(userId, scopeKey);
  memoryConversationSessions.delete(storageKey);

  try {
    sessionStorage.removeItem(storageKey);
    if (conversationToken) {
      sessionStorage.removeItem(
        `${CHAT_CONVERSATION_FEEDBACK_PREFIX}:${conversationToken}`,
      );
    }
  } catch {
    // Stan aplikacji zostanie wyczyszczony także bez dostępu do sessionStorage.
  }
};
